import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Checkbox } from './ui/checkbox';
import { api } from '../lib/api';
import { PAYMENT_METHODS } from '../lib/constants';
import { formatCurrency } from '@/lib/format';
import { toast } from 'sonner';
import { AlertCircle, CheckCircle2, Info, Layers, User } from 'lucide-react';

function toIstanbulWallClock(isoString) {
    if (!isoString) return '';
    try {
        const d = new Date(isoString);
        if (isNaN(d)) return '';
        const options = { timeZone: 'Europe/Istanbul', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false };
        const parts = new Intl.DateTimeFormat('tr-TR', options).formatToParts(d);
        const p = {};
        parts.forEach(part => { p[part.type] = part.value; });
        return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
    } catch (e) {
        return '';
    }
}

function fromIstanbulWallClockToISO(wallClockStr) {
    if (!wallClockStr) return null;
    return wallClockStr + ":00+03:00";
}

export default function CollectionModal({ open, onOpenChange, job, customer = null, onSuccess, existingData = null, children }) {
    // Mode: 'single' (job-level) or 'multi' (customer-level payment allocation)
    const [activeTab, setActiveTab] = useState(customer ? 'multi' : 'single');

    // --- Single-Job State ---
    const [jobs, setJobs] = useState([]);
    const [formData, setFormData] = useState({
        job_id: job?.id || '',
        _original_collection_date: existingData?.collection_date || null,
        collection_date: existingData?.collection_date
            ? toIstanbulWallClock(existingData.collection_date)
            : toIstanbulWallClock(new Date().toISOString()),
        amount: existingData?.amount || '',
        payment_method: existingData?.payment_method || 'nakit',
        installment_id: existingData?.installment_id || 'none',
        reference: existingData?.reference || '',
        note: existingData?.note || ''
    });
    const [installments, setInstallments] = useState([]);
    const [receivable, setReceivable] = useState(null);

    // --- Multi-Job State ---
    const [customers, setCustomers] = useState([]);
    const [selectedCustomerId, setSelectedCustomerId] = useState(customer?.id || job?.customer_id || '');
    const [customerJobs, setCustomerJobs] = useState([]);
    const [customerJobFinancials, setCustomerJobFinancials] = useState({});
    const [customerSummary, setCustomerSummary] = useState(null);
    const [loadingCustomerData, setLoadingCustomerData] = useState(false);

    const [multiForm, setMultiForm] = useState({
        payment_date: toIstanbulWallClock(new Date().toISOString()),
        total_amount: '',
        payment_method: 'nakit',
        reference: '',
        note: '',
        issue_receipt: true,
    });
    const [allocations, setAllocations] = useState({}); // { [jobId]: string }

    const [saving, setSaving] = useState(false);
    const submitting = useRef(false);
    const requests = useRef({});
    const completed = useRef(false);
    const postPayment = async (url, payload) => {
        const signature = JSON.stringify(payload);
        const previous = requests.current[url];
        const entry = previous?.signature === signature ? previous : { signature, key: crypto.randomUUID() };
        requests.current[url] = entry;
        return api.post(url, payload, { headers: { 'Idempotency-Key': entry.key } });
    };

    // Initial data fetch when dialog opens
    useEffect(() => {
        if (!open) return;
        completed.current = false;
        requests.current = {};

        if (!existingData) {
            // Fetch jobs for single-job mode dropdown
            if (!job) {
                fetchJobs();
            }
            // Fetch customers for multi-job mode
            fetchCustomers();
        }

        if (customer?.id) {
            setSelectedCustomerId(customer.id);
            setActiveTab('multi');
        } else if (job?.customer_id) {
            setSelectedCustomerId(job.customer_id);
        }
    // eslint-disable-next-line
    }, [open]);

    // Single job details
    useEffect(() => {
        if (formData.job_id) {
            fetchJobDetails(formData.job_id);
        } else {
            setInstallments([]);
            setReceivable(null);
        }
    }, [formData.job_id]);

    // Multi-job customer selection changed
    useEffect(() => {
        if (selectedCustomerId && activeTab === 'multi') {
            loadCustomerDetails(selectedCustomerId);
        } else if (!selectedCustomerId) {
            setCustomerJobs([]);
            setCustomerJobFinancials({});
            setCustomerSummary(null);
            setAllocations({});
        }
    }, [selectedCustomerId, activeTab]);

    const fetchJobs = async () => {
        try {
            const res = await api.get('/jobs?page_size=200');
            setJobs(res.data.items || []);
        } catch (e) {
            toast.error("İşler yüklenemedi");
        }
    };

    const fetchCustomers = async () => {
        try {
            const res = await api.get('/customers?page_size=200&is_active=true');
            setCustomers(res.data.items || []);
        } catch (e) {
            console.error("Müşteriler yüklenemedi", e);
        }
    };

    const fetchJobDetails = async (jobId) => {
        try {
            const [instRes, recvRes] = await Promise.all([
                api.get(`/jobs/${jobId}/installments`),
                api.get(`/jobs/${jobId}/receivables`)
            ]);
            setInstallments(instRes.data);
            setReceivable(recvRes.data);
        } catch (e) {
            console.error(e);
        }
    };

    const loadCustomerDetails = async (custId) => {
        setLoadingCustomerData(true);
        try {
            const [jobsRes, summaryRes] = await Promise.all([
                api.get(`/customers/${custId}/jobs?page_size=200`),
                api.get(`/customers/${custId}/financial-summary`)
            ]);

            const jList = jobsRes.data.items || [];
            setCustomerJobs(jList);
            setCustomerSummary(summaryRes.data);

            // Fetch financials for each job to display accurate outstanding balance
            const finMap = {};
            await Promise.all(
                jList.map(async (j) => {
                    try {
                        const fRes = await api.get(`/jobs/${j.id}/financials`);
                        finMap[j.id] = fRes.data;
                    } catch (err) {
                        finMap[j.id] = {
                            sales_amount: Number(j.sales_amount || 0),
                            total_collected: 0,
                            outstanding_balance: Number(j.sales_amount || 0)
                        };
                    }
                })
            );
            setCustomerJobFinancials(finMap);
            setAllocations({});
        } catch (e) {
            toast.error("Müşteri finansal detayları yüklenemedi");
            console.error(e);
        } finally {
            setLoadingCustomerData(false);
        }
    };

    // Allocation helpers
    const handleAllocationChange = (jobId, val) => {
        setAllocations(prev => ({
            ...prev,
            [jobId]: val
        }));
    };

    const allocatedSum = Object.values(allocations).reduce((acc, v) => acc + (parseFloat(v) || 0), 0);
    const totalMultiAmount = parseFloat(multiForm.total_amount) || 0;
    const unallocatedAmount = Math.round((totalMultiAmount - allocatedSum) * 100) / 100;

    const fillJobBalance = (jobId) => {
        const jobFin = customerJobFinancials[jobId];
        const jobBalance = jobFin ? Number(jobFin.outstanding_balance) : 0;
        const currentOtherAllocated = Object.entries(allocations)
            .filter(([id]) => id !== jobId)
            .reduce((acc, [_, v]) => acc + (parseFloat(v) || 0), 0);

        const availableFromTotal = Math.max(0, totalMultiAmount - currentOtherAllocated);
        // Fill up to remaining job balance or available amount
        const fillAmount = availableFromTotal > 0 && availableFromTotal < jobBalance
            ? availableFromTotal
            : Math.max(0, jobBalance);

        setAllocations(prev => ({
            ...prev,
            [jobId]: fillAmount.toFixed(2)
        }));
    };

    // Single-job submit
    const handleSingleSubmit = async (e) => {
        e.preventDefault();
        if (submitting.current || completed.current) return;

        if (receivable && Number(formData.amount) > Number(receivable.remaining_receivable) && !existingData) {
            if (!window.confirm(`Girdiğiniz tutar kalan alacaktan (${formatCurrency(receivable.remaining_receivable)}) büyük. Devam etmek istiyor musunuz?`)) {
                return;
            }
        }

        submitting.current = true;
        setSaving(true);
        try {
            const payload = { ...formData };
            if (payload.installment_id === 'none') {
                payload.installment_id = null;
            }

            if (existingData && payload.collection_date === toIstanbulWallClock(existingData.collection_date)) {
                payload.collection_date = formData._original_collection_date;
            } else {
                payload.collection_date = fromIstanbulWallClockToISO(payload.collection_date);
            }
            delete payload._original_collection_date;

            if (existingData) {
                await api.put(`/collections/${existingData.id}`, payload);
                toast.success('Tahsilat güncellendi');
            } else {
                const selectedJob = job?.id === payload.job_id ? job : jobs.find(j => j.id === payload.job_id);
                const customerId = selectedJob?.customer_id || job?.customer_id;
                if (customerId) {
                    // Canonical model for new collections: customer_payments + one allocation.
                    await postPayment('/customer-payments', {
                        customer_id: customerId,
                        payment_date: payload.collection_date,
                        total_amount: Number(payload.amount),
                        payment_method: payload.payment_method,
                        reference: payload.reference || null,
                        note: payload.note || null,
                        allocations: [{
                            job_id: payload.job_id,
                            installment_id: payload.installment_id || null,
                            amount: Number(payload.amount),
                        }],
                    });
                } else {
                    // Job is not linked to a customer record: customer_payments cannot be used.
                    await postPayment('/collections', payload);
                }
                toast.success('Tahsilat eklendi');
            }
            completed.current = true;
            onSuccess();
            onOpenChange(false);
        } catch (e) {
            toast.error('Tahsilat kaydedilemedi');
        } finally {
            submitting.current = false;
            setSaving(false);
        }
    };

    // Multi-job submit
    const handleMultiSubmit = async (e) => {
        e.preventDefault();
        if (submitting.current || completed.current) return;

        if (!selectedCustomerId) {
            toast.error("Lütfen bir müşteri seçiniz");
            return;
        }

        if (totalMultiAmount <= 0) {
            toast.error("Lütfen geçerli bir ödeme tutarı giriniz");
            return;
        }

        if (allocatedSum > totalMultiAmount + 0.001) {
            toast.error("Toplam tahsis edilen tutar, ödeme tutarını aşamaz!");
            return;
        }

        submitting.current = true;
        setSaving(true);
        try {
            const validAllocations = Object.entries(allocations)
                .filter(([_, amt]) => parseFloat(amt) > 0)
                .map(([jId, amt]) => ({
                    job_id: jId,
                    amount: parseFloat(amt)
                }));

            const payload = {
                customer_id: selectedCustomerId,
                payment_date: fromIstanbulWallClockToISO(multiForm.payment_date),
                total_amount: totalMultiAmount,
                payment_method: multiForm.payment_method,
                reference: multiForm.reference || null,
                note: multiForm.note || null,
                allocations: validAllocations
            };

            const paymentRes = await postPayment('/customer-payments', payload);
            const paymentId = paymentRes.data.id;

            if (multiForm.issue_receipt) {
                try {
                    await api.post(`/customer-payments/${paymentId}/receipt`);
                    toast.success("Ödeme kaydedildi ve tahsilat makbuzu oluşturuldu");
                } catch (rErr) {
                    toast.warning("Ödeme kaydedildi ancak makbuz oluşturulurken hata oluştu");
                }
            } else {
                toast.success("Çoklu iş tahsilatı başarıyla kaydedildi");
            }

            completed.current = true;
            onSuccess();
            onOpenChange(false);
        } catch (err) {
            const msg = err.response?.data?.detail || "Tahsilat kaydedilemedi";
            toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
        } finally {
            submitting.current = false;
            setSaving(false);
        }
    };

    const isEditMode = !!existingData;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className={activeTab === 'multi' && !isEditMode ? "sm:max-w-[760px] max-h-[92vh] flex flex-col overflow-y-auto" : "sm:max-w-[500px] max-h-[92vh] overflow-y-auto"}>
                <DialogHeader>
                    <DialogTitle>{isEditMode ? 'Tahsilat Düzenle' : 'Yeni Tahsilat'}</DialogTitle>
                </DialogHeader>

                {!isEditMode ? (
                    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full flex-1 shrink-0 flex flex-col overflow-hidden">
                        <TabsList className="grid w-full grid-cols-2 mb-3">
                            <TabsTrigger value="single" className="flex items-center gap-1.5">
                                <Layers className="w-4 h-4" />
                                <span>Tek İş Tahsilatı</span>
                            </TabsTrigger>
                            <TabsTrigger value="multi" className="flex items-center gap-1.5">
                                <User className="w-4 h-4" />
                                <span>Müşteri / Çoklu İş Tahsilatı</span>
                            </TabsTrigger>
                        </TabsList>

                        {/* SINGLE JOB TAB CONTENT */}
                        <TabsContent value="single" className="mt-0">
                            <form onSubmit={handleSingleSubmit}>
                                <div className="grid gap-4 py-2">
                                    {!job && (
                                        <div className="space-y-2">
                                            <Label>İş / Müşteri</Label>
                                            <Select value={formData.job_id} onValueChange={v => setFormData({...formData, job_id: v})}>
                                                <SelectTrigger>
                                                    <SelectValue placeholder="İş seçin..." />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {jobs.map(j => (
                                                        <SelectItem key={j.id} value={j.id}>{j.customer_name} ({j.job_number})</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    )}

                                    {receivable && (
                                        <div className="bg-gray-50 p-3 rounded-md text-sm grid grid-cols-2 gap-2 border">
                                            <div><span className="text-gray-500 block">Satış Tutarı</span> <span className="font-medium">{formatCurrency(receivable.sales_amount)}</span></div>
                                            <div><span className="text-gray-500 block">Kalan Alacak</span> <span className="font-medium text-gp-red">{formatCurrency(receivable.remaining_receivable)}</span></div>
                                        </div>
                                    )}

                                    <div className="space-y-2">
                                        <Label>Tarih & Saat</Label>
                                        <Input
                                            type="datetime-local"
                                            required
                                            value={formData.collection_date}
                                            onChange={e => setFormData({...formData, collection_date: e.target.value})}
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label>Tutar (TL)</Label>
                                        <Input
                                            type="number"
                                            step="0.01"
                                            min="0.01"
                                            required
                                            value={formData.amount}
                                            onChange={e => setFormData({...formData, amount: e.target.value})}
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label>Tahsilat Yöntemi</Label>
                                        <Select value={formData.payment_method} onValueChange={v => setFormData({...formData, payment_method: v})}>
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {Object.entries(PAYMENT_METHODS).map(([k, v]) => (
                                                    <SelectItem key={k} value={k}>{v}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    {installments.length > 0 && (
                                        <div className="space-y-2">
                                            <Label>Taksit Eşleştirme (İsteğe bağlı)</Label>
                                            <Select value={formData.installment_id} onValueChange={v => setFormData({...formData, installment_id: v})}>
                                                <SelectTrigger>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="none">Seçilmedi</SelectItem>
                                                    {installments.map(inst => (
                                                        <SelectItem key={inst.id} value={inst.id}>
                                                            Vade: {new Date(inst.due_date).toLocaleDateString()} - {formatCurrency(inst.planned_amount)} ({inst.status})
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    )}

                                    <div className="space-y-2">
                                        <Label>Açıklama / Not</Label>
                                        <Textarea
                                            value={formData.note}
                                            onChange={e => setFormData({...formData, note: e.target.value})}
                                        />
                                    </div>
                                </div>

                                <DialogFooter className="mt-4">
                                    <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>İptal</Button>
                                    <Button type="submit" disabled={saving || !formData.job_id} className="bg-brand-red hover:bg-brand-red-hover text-white">
                                        {saving ? 'Kaydediliyor...' : 'Kaydet'}
                                    </Button>
                                </DialogFooter>
                            </form>
                        </TabsContent>

                        {/* MULTI JOB TAB CONTENT */}
                        <TabsContent value="multi" className="mt-0 flex-1 overflow-y-auto pr-1">
                            <form onSubmit={handleMultiSubmit} className="space-y-4">
                                {/* Customer Selector */}
                                <div className="space-y-1.5">
                                    <Label>Müşteri (Cari)</Label>
                                    <Select value={selectedCustomerId} onValueChange={setSelectedCustomerId}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Müşteri seçin..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {customers.map(c => (
                                                <SelectItem key={c.id} value={c.id}>
                                                    {c.customer_number} • {c.display_name} {c.company_name ? `(${c.company_name})` : ''}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* Customer Summary Card */}
                                {customerSummary && (
                                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                                        <div>
                                            <span className="text-slate-500 block">Toplam Satış</span>
                                            <span className="font-semibold text-slate-800">{formatCurrency(customerSummary.total_sales)}</span>
                                        </div>
                                        <div>
                                            <span className="text-slate-500 block">Toplam Tahsilat</span>
                                            <span className="font-semibold text-emerald-700">{formatCurrency(customerSummary.total_collections)}</span>
                                        </div>
                                        <div>
                                            <span className="text-slate-500 block">Açık Alacak (İş Borçları)</span>
                                            <span className="font-semibold text-rose-600">{formatCurrency(customerSummary.outstanding_receivables)}</span>
                                        </div>
                                        <div>
                                            <span className="text-slate-500 block">Net Bakiye</span>
                                            <span className={`font-semibold ${customerSummary.net_balance > 0 ? 'text-rose-600' : 'text-slate-800'}`}>
                                                {formatCurrency(customerSummary.net_balance)}
                                            </span>
                                            {Number(customerSummary.customer_credit_balance) > 0 && (
                                                <span className="text-[10px] text-blue-600 block">Avans: {formatCurrency(customerSummary.customer_credit_balance)}</span>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Payment Details Grid */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div className="space-y-1.5">
                                        <Label>Ödeme Tutarı (TL) *</Label>
                                        <Input
                                            type="number"
                                            step="0.01"
                                            min="0.01"
                                            required
                                            value={multiForm.total_amount}
                                            onChange={e => setMultiForm({...multiForm, total_amount: e.target.value})}
                                            placeholder="0.00"
                                            className="font-medium text-base"
                                        />
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label>Tahsilat Yöntemi</Label>
                                        <Select value={multiForm.payment_method} onValueChange={v => setMultiForm({...multiForm, payment_method: v})}>
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {Object.entries(PAYMENT_METHODS).map(([k, v]) => (
                                                    <SelectItem key={k} value={k}>{v}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label>Tarih & Saat</Label>
                                        <Input
                                            type="datetime-local"
                                            required
                                            value={multiForm.payment_date}
                                            onChange={e => setMultiForm({...multiForm, payment_date: e.target.value})}
                                        />
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label>Referans / Dekont No</Label>
                                        <Input
                                            type="text"
                                            placeholder="Opsiyonel"
                                            value={multiForm.reference}
                                            onChange={e => setMultiForm({...multiForm, reference: e.target.value})}
                                        />
                                    </div>
                                </div>

                                {/* Job Allocation Section */}
                                <div className="space-y-2 pt-1">
                                    <div className="flex justify-between items-center">
                                        <Label className="text-sm font-semibold text-slate-800">
                                            İşlere Tahsis Et {customerJobs.length > 0 && `(${customerJobs.length} İş)`}
                                        </Label>
                                        <span className="text-xs text-slate-500">
                                            Tahsis zorunlu değildir; kalan tutar avans olarak saklanır.
                                        </span>
                                    </div>

                                    {loadingCustomerData ? (
                                        <div className="text-center py-4 text-xs text-slate-500">İşler ve bakiyeler yükleniyor...</div>
                                    ) : customerJobs.length === 0 ? (
                                        <div className="text-center py-4 bg-slate-50 border rounded-md text-xs text-slate-500">
                                            Bu müşteriye ait aktif iş bulunamadı. Ödeme cari avans olarak kaydedilebilir.
                                        </div>
                                    ) : (
                                        <div className="border rounded-md divide-y max-h-56 overflow-y-auto">
                                            {customerJobs.map(j => {
                                                const fin = customerJobFinancials[j.id];
                                                const outstanding = fin ? Number(fin.outstanding_balance) : 0;
                                                const sales = fin ? Number(fin.sales_amount) : Number(j.sales_amount || 0);
                                                const collected = fin ? Number(fin.total_collected) : 0;
                                                const allocatedVal = allocations[j.id] || '';

                                                return (
                                                    <div key={j.id} className="p-2.5 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs hover:bg-slate-50/50">
                                                        <div className="flex-1">
                                                            <div className="font-semibold text-slate-800">
                                                                {j.job_number} • {j.title || 'Başlıksız İş'}
                                                            </div>
                                                            <div className="text-slate-500 text-[11px] flex gap-3 mt-0.5">
                                                                <span>Satış: {formatCurrency(sales)}</span>
                                                                <span>Tahsilat: {formatCurrency(collected)}</span>
                                                                <span className={outstanding > 0 ? "font-semibold text-rose-600" : "text-emerald-600 font-semibold"}>
                                                                    Kalan: {formatCurrency(outstanding)}
                                                                </span>
                                                            </div>
                                                        </div>

                                                        <div className="flex items-center gap-2">
                                                            <Button
                                                                type="button"
                                                                variant="outline"
                                                                size="sm"
                                                                className="h-8 text-[11px] px-2 text-slate-600"
                                                                onClick={() => fillJobBalance(j.id)}
                                                            >
                                                                Bakiyeyi Kapat
                                                            </Button>
                                                            <Input
                                                                type="number"
                                                                step="0.01"
                                                                min="0"
                                                                placeholder="0.00"
                                                                value={allocatedVal}
                                                                onChange={e => handleAllocationChange(j.id, e.target.value)}
                                                                className="w-28 h-8 text-xs text-right font-medium"
                                                            />
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>

                                {/* Allocation Real-Time Reconciliation Bar */}
                                {totalMultiAmount > 0 && (
                                    <div className="bg-slate-50 border rounded-md p-2.5 text-xs space-y-1.5">
                                        <div className="flex justify-between items-center font-medium">
                                            <span>Toplam Ödeme: {formatCurrency(totalMultiAmount)}</span>
                                            <span>Tahsis Edilen: {formatCurrency(allocatedSum)}</span>
                                            <span className={unallocatedAmount < 0 ? "text-rose-600 font-bold" : "text-slate-700"}>
                                                Boşta / Avans: {formatCurrency(Math.max(0, unallocatedAmount))}
                                            </span>
                                        </div>

                                        {allocatedSum > totalMultiAmount + 0.001 ? (
                                            <div className="flex items-center gap-1.5 text-rose-600 text-xs font-semibold">
                                                <AlertCircle className="w-4 h-4 shrink-0" />
                                                <span>Tahsis edilen toplam tutar ödeme tutarından {formatCurrency(allocatedSum - totalMultiAmount)} fazladır!</span>
                                            </div>
                                        ) : unallocatedAmount > 0 ? (
                                            <div className="flex items-center gap-1.5 text-blue-600 text-[11px]">
                                                <Info className="w-3.5 h-3.5 shrink-0" />
                                                <span>{formatCurrency(unallocatedAmount)} tutar işlere dağıtılmadı, müşteri cari hesabına avans kaydedilecektir.</span>
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-1.5 text-emerald-600 text-[11px]">
                                                <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                                                <span>Ödeme tutarının tamamı işlere eksiksiz tahsis edildi.</span>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Notes and Receipt Options */}
                                <div className="space-y-1.5">
                                    <Label>Açıklama / Not</Label>
                                    <Textarea
                                        value={multiForm.note}
                                        onChange={e => setMultiForm({...multiForm, note: e.target.value})}
                                        placeholder="İsteğe bağlı tahsilat notu"
                                        rows={2}
                                    />
                                </div>

                                <div className="flex items-center space-x-2 pt-1">
                                    <Checkbox
                                        id="issue-receipt-chk"
                                        checked={multiForm.issue_receipt}
                                        onCheckedChange={v => setMultiForm({...multiForm, issue_receipt: !!v})}
                                    />
                                    <Label htmlFor="issue-receipt-chk" className="text-xs cursor-pointer font-medium text-slate-700">
                                        Resmi Tahsilat Makbuzu (A5 PDF) oluştur ve numaralandır
                                    </Label>
                                </div>

                                <DialogFooter className="pt-2">
                                    <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>İptal</Button>
                                    <Button
                                        type="submit"
                                        disabled={saving || !selectedCustomerId || totalMultiAmount <= 0 || allocatedSum > totalMultiAmount + 0.001}
                                        className="bg-brand-red hover:bg-brand-red-hover text-white"
                                    >
                                        {saving ? 'Kaydediliyor...' : 'Tahsilatı Onayla ve Kaydet'}
                                    </Button>
                                </DialogFooter>
                            </form>
                        </TabsContent>
                    </Tabs>
                ) : (
                    /* Existing single-job edit form */
                    <form onSubmit={handleSingleSubmit}>
                        <div className="grid gap-4 py-4">
                            <div className="space-y-2">
                                <Label>Tarih & Saat</Label>
                                <Input
                                    type="datetime-local"
                                    required
                                    value={formData.collection_date}
                                    onChange={e => setFormData({...formData, collection_date: e.target.value})}
                                />
                            </div>

                            <div className="space-y-2">
                                <Label>Tutar (TL)</Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    min="0.01"
                                    required
                                    value={formData.amount}
                                    onChange={e => setFormData({...formData, amount: e.target.value})}
                                />
                            </div>

                            <div className="space-y-2">
                                <Label>Tahsilat Yöntemi</Label>
                                <Select value={formData.payment_method} onValueChange={v => setFormData({...formData, payment_method: v})}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(PAYMENT_METHODS).map(([k, v]) => (
                                            <SelectItem key={k} value={k}>{v}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <Label>Açıklama / Not</Label>
                                <Textarea
                                    value={formData.note}
                                    onChange={e => setFormData({...formData, note: e.target.value})}
                                />
                            </div>
                        </div>

                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>İptal</Button>
                            <Button type="submit" disabled={saving} className="bg-brand-red hover:bg-brand-red-hover text-white">
                                {saving ? 'Kaydediliyor...' : 'Kaydet'}
                            </Button>
                        </DialogFooter>
                    </form>
                )}
                {children}
            </DialogContent>
        </Dialog>
    );
}
